import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Check } from "lucide-react";
import LoadingSpinner from "../components/UI/LoadingSpinner";
import { apiService } from "../services/apiService";

const ChangePasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    // CRITICAL: Clear any existing user session to prevent auto-login conflicts
    const existingUser = localStorage.getItem('userDetails');
    if (existingUser) {
      localStorage.removeItem('userDetails');
      localStorage.removeItem('impersonating');
      console.log('🧹 Cleared existing user session for password reset');
    }
    
    // Set a flag to prevent auto-login while on this page
    sessionStorage.setItem('preventAutoLogin', 'true');
    console.log('🛑 Set preventAutoLogin flag');
    
    const tokenParam = searchParams.get("token");
    if (!tokenParam) {
      setError("Invalid or missing reset token. Please request a new password reset.");
    } else {
      setToken(tokenParam);
    }
    
    // Cleanup function to remove the flag when leaving the page
    return () => {
      sessionStorage.removeItem('preventAutoLogin');
      console.log('🧹 Removed preventAutoLogin flag');
    };
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError("Invalid reset token. Please request a new password reset.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await apiService.changePassword(token, password);
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err: any) {
      console.error("Password change error:", err);
      setError(
        err.response?.data?.message ||
        "Failed to change password. The reset link may have expired. Please request a new one."
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full">
          <div className="bg-white p-8 rounded-xl shadow-lg text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Password Changed Successfully</h2>
            <p className="text-gray-600 mb-6">
              Your password has been updated. You will be redirected to the login page in a few seconds.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center px-4 py-2 text-white rounded-lg transition-colors"
              style={{background: "linear-gradient(to right, #4834d4, #341f97)"}}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "linear-gradient(to right, #341f97, #2a1a7a)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "linear-gradient(to right, #4834d4, #341f97)";
              }}
            >
              Go to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Welcome */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center relative overflow-hidden" style={{background: "linear-gradient(to right, #4834d4, #341f97)"}}>
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="text-center text-white relative z-10">
          <img 
            src="/logoBlack.png" 
            alt="ITP360" 
            className="h-16 w-auto mx-auto mb-8 filter brightness-0 invert"
          />
          <h1 className="text-4xl font-bold mb-4">Set New Password</h1>
          <p className="text-white text-xl font-light opacity-90">
            Create a secure password for your account
          </p>
        </div>
      </div>

      {/* Right side - Password form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="/logoBlack.png" 
              alt="ITP360" 
              className="h-12 w-auto mx-auto mb-6"
            />
            <h1 className="text-2xl font-bold text-gray-900">Set New Password</h1>
          </div>

          <div className="hidden lg:block mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Set New Password</h2>
            <p className="text-gray-600 mt-2">Enter your new password below</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {!token ? (
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Reset Link</h2>
              <p className="text-gray-600 mb-6">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="inline-flex items-center px-4 py-2 text-white rounded-lg transition-colors"
                style={{background: "linear-gradient(to right, #4834d4, #341f97)"}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "linear-gradient(to right, #341f97, #2a1a7a)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "linear-gradient(to right, #4834d4, #341f97)";
                }}
              >
                Request New Reset Link
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-lg space-y-6">
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter your new password"
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Password must be at least 8 characters long</p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Confirm your new password"
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{
                  background: "linear-gradient(to right, #4834d4, #341f97)",
                  "--tw-ring-color": "#4834d4"
                } as React.CSSProperties & {"--tw-ring-color": string}}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.background = "linear-gradient(to right, #341f97, #2a1a7a)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.background = "linear-gradient(to right, #4834d4, #341f97)";
                  }
                }}
              >
                {loading ? (
                  <LoadingSpinner size="small" className="text-white" />
                ) : (
                  "Change Password"
                )}
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm font-medium transition-colors"
                  style={{color: "#4834d4"}}
                  onMouseEnter={(e) => e.currentTarget.style.color = "#341f97"}
                  onMouseLeave={(e) => e.currentTarget.style.color = "#4834d4"}
                >
                  Back to Sign In
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
